import React, { useState } from "react";

/**
 * BaoBrain Tracker Success Modal (v2)
 *
 * Naming note:
 *   The wrapper class is `bao-tracker-success-v2` — chosen to avoid any
 *   possible CSS collision with old `.flashcard-congrats` styles that may
 *   still exist somewhere in the codebase. All visual styling is inline,
 *   so external CSS files cannot override it.
 *
 * Layout priority (top → bottom):
 *   1. Teal celebratory icon (brand-consistent)
 *   2. "You're all set!" title
 *   3. Confirmation of what's connected (the shop domain)
 *   4. Single primary CTA — Go to Dashboard
 *   5. Quiet info: 24–72 hour note
 *   6. Quiet text link: Import historical orders
 *
 * Backfill is a text link, not a colored card. The success modal should
 * feel like a celebration, not a second decision screen.
 *
 * Backwards-compat: still exports `FlashCardCongrats` as default so any
 * existing `import FlashCardCongrats from ...` continues to work.
 */
function BaoTrackerSuccessModal({
  title = "You're all set!",
  shopDomain = null,
  message = null,
  ctaLabel = "Go to Dashboard",
  onCta,
  onClose,
  className = "",
  showBackfill = true,
  onBackfill = null,
}) {
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillStarted, setBackfillStarted] = useState(false);

  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      if (onBackfill) {
        await onBackfill();
      }
      setBackfillStarted(true);
    } catch (error) {
      console.error('Backfill error:', error);
    } finally {
      setBackfillLoading(false);
    }
  };

  // Default subtitle: confirm what's connected
  const defaultSubtitle = shopDomain ? (
    <React.Fragment>
      Tracking is live on{' '}
      <strong style={baoStyles.shopDomain}>{shopDomain}</strong>
    </React.Fragment>
  ) : (
    'Tracking is live on your store'
  );

  return (
    <div className={`bao-tracker-success-v2 ${className}`} style={baoStyles.container}>
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          style={baoStyles.closeButton}
          aria-label="Close"
          className="bao-success-close-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Success Icon — teal gradient circle, brand-consistent */}
      <div style={baoStyles.iconWrapper}>
        <div style={baoStyles.iconCircle} className="bao-success-icon-circle">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12l5 5 9-10"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h2 style={baoStyles.title}>{title}</h2>

      {/* Subtitle — confirms what's connected */}
      <p style={baoStyles.subtitle}>
        {message || defaultSubtitle}
      </p>

      {/* Primary CTA — single, prominent, brand-teal */}
      {onCta && (
        <button
          onClick={onCta}
          style={baoStyles.primaryButton}
          className="bao-success-primary-btn"
        >
          {ctaLabel}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Divider */}
      <div style={baoStyles.divider} />

      {/* Secondary info: 24-72 hour note */}
      <div style={baoStyles.infoRow}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={baoStyles.infoIcon}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span style={baoStyles.infoText}>
          Your insights will populate as visitors come to your store —
          usually within <strong>24–72 hours</strong>.
        </span>
      </div>

      {/* Backfill — quiet text link, not a colored card */}
      {showBackfill && onBackfill && !backfillStarted && (
        <div style={baoStyles.infoRow}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={baoStyles.infoIcon}>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
          <span style={baoStyles.infoText}>
            Want trends now?{' '}
            <button
              onClick={handleBackfill}
              disabled={backfillLoading}
              style={baoStyles.linkButton}
              className="bao-success-link-btn"
            >
              {backfillLoading ? 'Starting…' : 'Import last year of orders →'}
            </button>
          </span>
        </div>
      )}

      {/* Backfill Success Confirmation */}
      {backfillStarted && (
        <div style={baoStyles.backfillSuccess}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="#00A8A9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Import started — running in background</span>
        </div>
      )}

      {/* Scoped animations + hover states. Selectors are namespaced so
          they cannot affect or be affected by other components. */}
      <style>{`
        @keyframes baoSuccessScaleIn {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        .bao-tracker-success-v2 .bao-success-icon-circle {
          animation: baoSuccessScaleIn 0.4s ease-out;
        }
        .bao-tracker-success-v2 .bao-success-primary-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0, 168, 169, 0.4);
        }
        .bao-tracker-success-v2 .bao-success-link-btn:hover:not(:disabled) {
          text-decoration: underline;
        }
        .bao-tracker-success-v2 .bao-success-close-btn:hover {
          color: #374151;
        }
      `}</style>
    </div>
  );
}

// Inline styles namespaced under `baoStyles` so they cannot collide
// with any other `styles` constant in the project.
const baoStyles = {
  container: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '36px 32px 28px',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    fontFamily: 'inherit',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
  },
  iconWrapper: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'center',
  },
  iconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00A8A9 0%, #008f90 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0, 168, 169, 0.35)',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: '15px',
    lineHeight: 1.5,
    color: '#6b7280',
    margin: '0 0 24px 0',
  },
  shopDomain: {
    color: '#111827',
    fontWeight: 600,
  },
  primaryButton: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #00A8A9 0%, #008f90 100%)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 14px rgba(0, 168, 169, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  divider: {
    height: '1px',
    background: '#f3f4f6',
    margin: '24px 0 18px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    textAlign: 'left',
    padding: '4px 0',
    marginBottom: '10px',
  },
  infoIcon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  infoText: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#6b7280',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
    color: '#00A8A9',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  backfillSuccess: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: '#f0fdfa',
    border: '1px solid #99f6e4',
    borderRadius: '8px',
    marginTop: '12px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#0d9488',
  },
};

// Default export keeps the old name so existing imports keep working.
// New name is also exported for any code that wants to be explicit.
export { BaoTrackerSuccessModal };
export default BaoTrackerSuccessModal;